import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import {
    type CreateModelConfiguratorMutation,
    type CreateModelConfiguratorMutationVariables,
    CreateModelConfiguratorDocument,
} from '../../../api/mutations/createModelConfigurator';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { GenericStateOnPageContextProvider } from '../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useHandleError from '../../../utilities/useHandleError';
import ModelConfiguratorForm, {
    useModelConfiguratorFormValidator,
    defaultValues,
    type ModelConfiguratorFormValues,
    FORM_NAME,
    LIST_URL,
    useHandleModelConfiguratorAssets,
} from '../ModelConfiguratorDetailsPage/ModelConfiguratorForm';
import ModelConfiguratorProvider from '../ModelConfiguratorDetailsPage/ModelConfiguratorProvider';

const AddModelConfiguratorPage = () => {
    const { t } = useTranslation(['modelConfiguratorDetails']);
    const { permissions: accountPermissions } = useAccount();

    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();

    const validate = useModelConfiguratorFormValidator(true);

    const apolloClient = useApolloClient();

    const handleModelConfiguratorAssets = useHandleModelConfiguratorAssets();

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(accountPermissions, [permissionKind.createConfigurator]),
            source: {
                kind: ContentRefinementSourceKind.Event,
                action: ContentRefinementSourceAction.Add,
            },
        }),
        [accountPermissions]
    );

    const onSubmit = useHandleError<ModelConfiguratorFormValues>(
        async values => {
            const { modelId, moduleId, banner, mobileBanner, descriptionImages, ...settings } = values;
            const priceDisclaimer = {
                defaultValue: [{ defaultValue: '', overrides: [] }],
                overrides: [],
            };
            setLoading(true);
            message.loading({
                content: t('modelConfiguratorDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await apolloClient.mutate<
                CreateModelConfiguratorMutation,
                CreateModelConfiguratorMutationVariables
            >({
                mutation: CreateModelConfiguratorDocument,
                variables: {
                    modelId,
                    moduleId,
                    settings: {
                        ...settings,
                        priceDisclaimer,
                    },
                },
            });

            try {
                await handleModelConfiguratorAssets(data.modelConfigurator.id, values);
            } catch (error) {
                console.warn(error);
            }

            // inform about success
            message.success({
                content: t('modelConfiguratorDetails:messages.createSuccessful'),
                key: 'primary',
            });
            navigate(`/admin/configurators/${data.modelConfigurator.id}`);
        },
        [setLoading, t, apolloClient, navigate, handleModelConfiguratorAssets],
        undefined,
        () => {
            setLoading(false);
        }
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ModelConfiguratorProvider>
                <ConsolePageWithHeader
                    footer={[
                        <LoadingButton key="submit" form={FORM_NAME} htmlType="submit" type="primary">
                            {t('modelConfiguratorDetails:actions.create')}
                        </LoadingButton>,
                    ]}
                    onBack={() => navigate(LIST_URL)}
                    title={t('modelConfiguratorDetails:addTitle')}
                >
                    <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate} enableReinitialize>
                        {({ handleSubmit }) => (
                            <Form id={FORM_NAME} name={FORM_NAME} onSubmitCapture={handleSubmit}>
                                <FormAutoTouch />
                                <ModelConfiguratorForm />
                            </Form>
                        )}
                    </Formik>
                </ConsolePageWithHeader>
            </ModelConfiguratorProvider>
        </GenericStateOnPageContextProvider>
    );
};

export default AddModelConfiguratorPage;
